import { recoveryPoolInnerCall } from "./sdk";
import { ethers } from "ethers";
import { SHIELDED_POOL_JOINSPLIT_V2_INTERFACE } from "./sdk";
import { EVM_CONFIG } from "../config";
import {
  depositMergeEvidenceFromJournal,
  depositNoteFromJournal,
  verifyFreshPoolDepositReceipt,
} from "./depositEvidence";
import { verifyEvmDepositMergeReceipt } from "./depositMergeEvm";
import { assertFlatJoinSplitV2Note } from "./notes";
import type { JoinSplitNote } from "../joinsplit/types";
import {
  recoverFlatJoinSplitV2SwapOutput,
  SWAP_OUTPUT_EVENTS,
} from "./swapOutput";
import {
  listFlatJoinSplitV2SettlementJournal,
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementInputSuperseded,
  retireSupersededFlatJoinSplitV2SettlementJournalRecord,
  retireExpiredFlatDepositMergeJournalRecord,
  type FlatJoinSplitV2JournalRecord,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { type FlatJoinSplitV2LeafInsertion } from "./merkleIndex";
import { readJournalTreeHistory } from "./journalTreeHistory";
import {
  settlementIntentOf,
  settlementIntentsOf,
  transactionMatchesRecord,
} from "./settlementIntent";
import { PinnedHistory } from "./pinnedHistory";
import { privatePoolDeploymentBlock } from "./privatePools";
import { decodePrivateSwap } from "./privateSwapConfig";
import { reconcilePrivateSwapJournal } from "./privateSwapReconciliation";

const settlementHistory = new PinnedHistory<ethers.Log[]>(
  (logs) => logs.length <= 100_000,
);

const RECONCILE_INTERFACE = new ethers.Interface([
  "function nullifierUsed(bytes32 nullifier) view returns (bool)",
  "event Withdrawal(bytes32[] nullifiers, address indexed recipient, address token, uint256 amount, bytes32 changeCommitment)",
  "event WithdrawalBatch(bytes32[] nullifiers, address token, address[] recipients, uint256[] amounts, uint256 fee, bytes32 changeCommitment)",
  SWAP_OUTPUT_EVENTS.getEvent("PrivateSwap")!,
  "event PrivateBridge(bytes32[] nullifiers,address inputToken,uint256 inputAmount,address target,bytes32 orderId)",
]);
const WITHDRAWAL_EVENT = RECONCILE_INTERFACE.getEvent("Withdrawal");
const WITHDRAWAL_EVENT_TOPIC = WITHDRAWAL_EVENT.topicHash.toLowerCase();
const WITHDRAWAL_BATCH_EVENT = RECONCILE_INTERFACE.getEvent("WithdrawalBatch");
const WITHDRAWAL_BATCH_EVENT_TOPIC =
  WITHDRAWAL_BATCH_EVENT.topicHash.toLowerCase();
const SWAP_EVENT = RECONCILE_INTERFACE.getEvent("PrivateSwap")!;
const SWAP_EVENT_TOPIC = SWAP_EVENT.topicHash.toLowerCase();
const BRIDGE_EVENT = RECONCILE_INTERFACE.getEvent("PrivateBridge")!;
const BRIDGE_EVENT_TOPIC = BRIDGE_EVENT.topicHash.toLowerCase();
/** Journal kinds whose changeless settlements only surface as payout events. */
const WITHDRAWAL_EVIDENCE_KINDS: readonly string[] = [
  "withdraw",
  "withdraw_batch",
];

/** A withdrawal record closed because another settlement consumed exactly
 * its inputs: it can never execute, and it had no outputs to recover. */
export interface FlatJoinSplitV2SupersededRecord {
  record: FlatJoinSplitV2JournalRecord;
  txHash: string;
  blockNumber: number;
  /** One consumed input makes the old proof impossible; retain all its secrets. */
  nullifier?: string;
}

export interface FlatJoinSplitV2JournalReconciliationResult {
  records: FlatJoinSplitV2JournalRecord[];
  finalized: FlatJoinSplitV2JournalRecord[];
  ambiguous: FlatJoinSplitV2JournalRecord[];
  superseded: FlatJoinSplitV2SupersededRecord[];
  issues: Map<string, string>;
  /** Why each ambiguous record is not finalized yet, by record id. */
  reasons: Map<string, string>;
  safeBlock: number;
  safeBlockHash: string;
}

async function pinnedBlock(
  provider: ethers.Provider,
  number: number,
  expectedHash?: string,
): Promise<string> {
  const block = await provider.getBlock(number);
  if (!block?.hash || block.number !== number) {
    throw new Error("Settlement journal reconciliation block is unavailable");
  }
  const hash = block.hash.toLowerCase();
  if (expectedHash && expectedHash !== hash) {
    throw new Error("Settlement journal reconciliation block changed");
  }
  return hash;
}

async function nullifierUsed(
  provider: ethers.Provider,
  pool: string,
  nullifier: string,
  blockTag: number,
): Promise<boolean> {
  const data = RECONCILE_INTERFACE.encodeFunctionData("nullifierUsed", [
    nullifier,
  ]);
  const raw = await provider.call({ to: pool, data, blockTag } as any);
  return Boolean(
    RECONCILE_INTERFACE.decodeFunctionResult("nullifierUsed", raw)[0],
  );
}

/** Byte-identical to the journaled transaction, or carrying exactly the
 * journaled pool settlement: the relayer submits wallet settlements inside
 * its token bundle or a coordinator call, so the outer calldata differs. */
function exactTransactionMatches(
  transaction: ethers.TransactionResponse,
  record: FlatJoinSplitV2JournalRecord,
): boolean {
  return transactionMatchesRecord(
    {
      to: transaction.to,
      data: transaction.data,
      value: transaction.value,
      chainId: transaction.chainId,
    },
    record,
  );
}

/** Selector and target only: enough to tell which carrier a transaction
 * used, never its arguments. */
function describeCall(transaction: {
  to?: string | null;
  data?: string;
}): string {
  const data = String(transaction.data || "");
  const to = transaction.to ? String(transaction.to) : "0x";
  return `${data.slice(0, 10)}@${to.slice(0, 6)}…${to.slice(-4)}`;
}

/** Positive evidence, from a carrier that decodes exactly, that the
 * transaction settles this record's exact inputs under another proof (a
 * retry, or the same settlement proven again). Nullifiers spend once, so the
 * record's own settlement can never execute. */
function spendsSameInputsUnderAnotherProof(
  transaction: ethers.TransactionResponse,
  record: FlatJoinSplitV2JournalRecord,
): boolean {
  const journaled = settlementIntentOf(record.transaction.data);
  if (!journaled || journaled.nullifiers.length === 0) return false;
  const inputs = journaled.nullifiers.join(",");
  return settlementIntentsOf(transaction.data).some(
    (carried) =>
      carried.nullifiers.join(",") === inputs &&
      carried.proof !== journaled.proof,
  );
}

/** Why a confirmed transaction does not prove a record, in public terms
 * (nullifiers and proofs are on chain): selector and target alone cannot
 * tell a retried settlement from a foreign one. */
function mismatchReason(
  transaction: ethers.TransactionResponse,
  record: FlatJoinSplitV2JournalRecord,
): string {
  const shortHash = `${transaction.hash.slice(0, 10)}…`;
  const calls = `journaled ${describeCall(record.transaction)}, on-chain ${describeCall(transaction)}`;
  if (
    transaction.data.toLowerCase() === record.transaction.data.toLowerCase()
  ) {
    return `its transaction ${shortHash} carries the journaled calldata with a different value or chain (${calls})`;
  }
  return spendsSameInputsUnderAnotherProof(transaction, record)
    ? `its inputs were spent by ${shortHash} under a different proof (${calls})`
    : `its transaction ${shortHash} does not carry the journaled settlement (${calls})`;
}
function insertionFor(
  insertions: Map<string, FlatJoinSplitV2LeafInsertion>,
  note: JoinSplitNote,
): FlatJoinSplitV2LeafInsertion | undefined {
  return insertions.get(note.commitment.toLowerCase());
}

function nullifierSequenceKey(nullifiers: readonly string[]): string {
  return nullifiers.map((nullifier) => nullifier.toLowerCase()).join(":");
}

function bridgeIntent(record: FlatJoinSplitV2JournalRecord) {
  const decoded = SHIELDED_POOL_JOINSPLIT_V2_INTERFACE.parseTransaction({
    data: recoveryPoolInnerCall(record.transaction.data),
  });
  if (
    !decoded ||
    decoded.name !== "privateBridge" ||
    SHIELDED_POOL_JOINSPLIT_V2_INTERFACE.encodeFunctionData(
      decoded.fragment,
      decoded.args,
    ).toLowerCase() !==
      recoveryPoolInnerCall(record.transaction.data).toLowerCase()
  ) {
    throw new Error(
      "Bridge recovery requires the exact canonical privateBridge intent",
    );
  }
  const [spend, bridge] = decoded.args;
  const change = String(spend.changeCommitment).toLowerCase();
  if (
    nullifierSequenceKey(Array.from(spend.nullifiers, String)) !==
      nullifierSequenceKey(record.sourceNullifiers) ||
    record.sourceNullifiers.length === 0 ||
    BigInt(spend.amount) <= BigInt(spend.fee) ||
    BigInt(bridge.callValue) !== BigInt(record.transaction.value) ||
    (change === ethers.ZeroHash
      ? record.outputNotes.length !== 0
      : record.outputNotes.length !== 1 ||
        record.outputNotes[0].commitment.toLowerCase() !== change)
  ) {
    throw new Error(
      "Bridge recovery metadata does not match the recorded intent",
    );
  }
  return { spend, bridge };
}

function bridgeEventMatches(
  event: ethers.Result,
  intent: ReturnType<typeof bridgeIntent>,
): boolean {
  const { spend, bridge } = intent;
  return (
    nullifierSequenceKey(Array.from(event.nullifiers, String)) ===
      nullifierSequenceKey(Array.from(spend.nullifiers, String)) &&
    ethers.getAddress(event.inputToken) === ethers.getAddress(spend.token) &&
    BigInt(event.inputAmount) === BigInt(spend.amount) - BigInt(spend.fee) &&
    ethers.getAddress(event.target) === ethers.getAddress(bridge.target) &&
    // The adapter explicitly permits an unbound order ID when this is zero.
    (String(bridge.expectedOrderId).toLowerCase() === ethers.ZeroHash ||
      String(event.orderId).toLowerCase() ===
        String(bridge.expectedOrderId).toLowerCase())
  );
}

function requireBridgeReceipt(
  record: FlatJoinSplitV2JournalRecord,
  receipt: ethers.TransactionReceipt,
): void {
  const intent = bridgeIntent(record);
  const logs = receipt.logs.filter(
    (log) =>
      ethers.getAddress(log.address) === record.poolAddress &&
      log.topics[0]?.toLowerCase() === BRIDGE_EVENT_TOPIC,
  );
  if (logs.length !== 1) {
    throw new Error(
      "Bridge receipt must contain exactly one PrivateBridge event",
    );
  }
  const log = logs[0];
  if (
    log.removed ||
    log.blockNumber !== receipt.blockNumber ||
    log.blockHash.toLowerCase() !== receipt.blockHash.toLowerCase() ||
    log.transactionHash.toLowerCase() !== receipt.hash.toLowerCase() ||
    !bridgeEventMatches(
      RECONCILE_INTERFACE.decodeEventLog(BRIDGE_EVENT, log.data, log.topics),
      intent,
    )
  ) {
    throw new Error(
      "Bridge receipt event does not match the recorded settlement intent",
    );
  }
}

async function scanSettlementEventHashes(params: {
  provider: ethers.Provider;
  pool: string;
  records: readonly FlatJoinSplitV2JournalRecord[];
  fromBlock: number;
  toBlock: number;
  toBlockHash: string;
  chunkSize: number;
}): Promise<Map<string, Set<string>>> {
  const wanted = new Map<string, FlatJoinSplitV2JournalRecord[]>();
  for (const record of params.records.filter(
    (record) =>
      ((WITHDRAWAL_EVIDENCE_KINDS.includes(record.kind) &&
        record.outputNotes.length === 0) ||
        record.kind === "swap" ||
        record.kind === "bridge") &&
      record.sourceNullifiers.length > 0,
  )) {
    const key = nullifierSequenceKey(record.sourceNullifiers);
    wanted.set(key, [...(wanted.get(key) ?? []), record]);
  }
  const hashes = new Map<string, Set<string>>();
  if (wanted.size === 0) return hashes;

  const pool = ethers.getAddress(params.pool);
  await settlementHistory.read({
    provider: params.provider as any,
    key: `${pool}:${params.fromBlock}`,
    block: params.toBlock,
    hash: params.toBlockHash,
    build: async (previous) => {
      const collected = [...(previous?.value || [])];
      for (
        let fromBlock = previous ? previous.block + 1 : params.fromBlock;
        fromBlock <= params.toBlock;
        fromBlock += params.chunkSize
      ) {
        const toBlock = Math.min(
          params.toBlock,
          fromBlock + params.chunkSize - 1,
        );
        const logs = await params.provider.getLogs({
          address: pool,
          topics: [
            [
              WITHDRAWAL_EVENT_TOPIC,
              WITHDRAWAL_BATCH_EVENT_TOPIC,
              SWAP_EVENT_TOPIC,
              BRIDGE_EVENT_TOPIC,
            ],
          ],
          fromBlock,
          toBlock,
        });
        if (
          !Array.isArray(logs) ||
          logs.some(
            (log) =>
              !Number.isSafeInteger(log.blockNumber) ||
              log.blockNumber < fromBlock ||
              log.blockNumber > toBlock,
          )
        )
          throw new Error(
            "Settlement journal received an invalid withdrawal log range",
          );
        collected.push(...logs);
      }
      return collected;
    },
    validate: (logs) => {
      for (const log of logs) {
        const topic = log.topics[0]?.toLowerCase();
        const isBatch = topic === WITHDRAWAL_BATCH_EVENT_TOPIC;
        const isSwap = topic === SWAP_EVENT_TOPIC;
        const isBridge = topic === BRIDGE_EVENT_TOPIC;
        if (
          ethers.getAddress(log.address) !== pool ||
          (topic !== WITHDRAWAL_EVENT_TOPIC && !isBatch && !isSwap && !isBridge)
        ) {
          throw new Error(
            "Settlement journal received a foreign withdrawal log",
          );
        }
        if (
          log.removed ||
          !Number.isSafeInteger(log.blockNumber) ||
          log.blockNumber < params.fromBlock ||
          log.blockNumber > params.toBlock
        ) {
          throw new Error(
            "Settlement journal received an invalid withdrawal log",
          );
        }
        if (
          log.blockNumber === params.toBlock &&
          log.blockHash.toLowerCase() !== params.toBlockHash
        ) {
          throw new Error(
            "Settlement journal withdrawal log is outside the pinned safe block",
          );
        }
        if (!ethers.isHexString(log.transactionHash, 32)) {
          throw new Error(
            "Settlement journal withdrawal log has no transaction hash",
          );
        }
        const decoded = RECONCILE_INTERFACE.decodeEventLog(
          isBridge
            ? BRIDGE_EVENT
            : isSwap
              ? SWAP_EVENT
              : isBatch
                ? WITHDRAWAL_BATCH_EVENT
                : WITHDRAWAL_EVENT,
          log.data,
          log.topics,
        );
        const changeCommitment = String(decoded[isBatch ? 5 : 4]).toLowerCase();
        if (!isSwap && !isBridge && changeCommitment !== ethers.ZeroHash)
          continue;
        const key = nullifierSequenceKey(
          Array.from(decoded[0] as readonly string[], (value) => String(value)),
        );
        for (const record of wanted.get(key) ?? []) {
          if ((record.kind === "bridge") !== isBridge) continue;
          if ((record.kind === "swap") !== isSwap) continue;
          if (isBridge) {
            try {
              if (!bridgeEventMatches(decoded, bridgeIntent(record))) continue;
            } catch {
              // The per-record reconciliation below reports malformed intent;
              // one bad record must not prevent discovery for the other records.
              continue;
            }
          }
          const matching = hashes.get(record.id) ?? new Set<string>();
          matching.add(log.transactionHash.toLowerCase());
          hashes.set(record.id, matching);
        }
      }
    },
  });
  return hashes;
}

/**
 * Reconcile crash-, replacement-, and copied-transaction cases against one
 * hash-pinned, confirmation-safe pool view. Records that cannot be proven
 * finalized are retained verbatim as ambiguous recovery state, except
 * changeless withdrawals proven superseded, which are closed.
 */
export async function reconcileFlatJoinSplitV2SettlementJournal(params: {
  provider: ethers.Provider;
  scope: FlatJoinSplitV2JournalScope;
  deploymentBlock?: number;
  finalityBlocks?: number;
  chunkSize?: number;
}): Promise<FlatJoinSplitV2JournalReconciliationResult> {
  const records = await listFlatJoinSplitV2SettlementJournal(params.scope);
  const deploymentBlock =
    params.deploymentBlock ??
    privatePoolDeploymentBlock(params.scope.poolAddress);
  const finalityBlocks =
    params.finalityBlocks ?? EVM_CONFIG.JOINSPLIT_V2.FINALITY_BLOCKS;
  if (!Number.isSafeInteger(deploymentBlock) || deploymentBlock < 0) {
    throw new Error("Settlement journal deployment block is invalid");
  }
  if (!Number.isSafeInteger(finalityBlocks) || finalityBlocks < 0) {
    throw new Error("Settlement journal finality policy is invalid");
  }
  // No records means there is no settlement to reconcile. Balance sync still
  // pins and validates the chain independently; this is not a finality result.
  if (records.length === 0)
    return {
      records,
      finalized: [],
      ambiguous: [],
      superseded: [],
      issues: new Map(),
      reasons: new Map(),
      safeBlock: 0,
      safeBlockHash: ethers.ZeroHash,
    };
  const requestedChunkSize = params.chunkSize ?? 5_000;
  if (!Number.isSafeInteger(requestedChunkSize) || requestedChunkSize <= 0) {
    throw new Error("Settlement journal chunk size is invalid");
  }
  const chunkSize = Math.min(requestedChunkSize, 10_000);
  const [network, head] = await Promise.all([
    params.provider.getNetwork(),
    params.provider.getBlockNumber(),
  ]);
  if (BigInt(network.chainId) !== BigInt(params.scope.chainId)) {
    throw new Error("Settlement journal provider is on the wrong chain");
  }
  if (!Number.isSafeInteger(head) || head < 0)
    throw new Error("Settlement journal provider returned an invalid head");
  // The same confirmation-depth policy as Base. True chain finality remains
  // mandatory below before abandoning an expired or conflicting intent.
  const safeBlock = Math.max(0, head - finalityBlocks);
  const safeBlockHash = await pinnedBlock(params.provider, safeBlock);
  // Re-audit even records previously marked finalized. A cached state is not
  // authority after restart; only the current hash-pinned safe view may release
  // note secrets for delivery or deletion.
  const unresolved = records;
  if (unresolved.length === 0 || safeBlock < deploymentBlock) {
    return {
      records,
      finalized: [],
      ambiguous: unresolved,
      superseded: [],
      issues: new Map(),
      reasons: new Map(
        unresolved.map((record) => [
          record.id,
          "the safe block is before the pool deployment",
        ]),
      ),
      safeBlock,
      safeBlockHash,
    };
  }

  const treeIndexes = new Set<number>();
  const standardRecords = unresolved.filter(
    (record) =>
      !(record.kind === "swap" && decodePrivateSwap(record.transaction.data)),
  );
  for (const record of standardRecords) {
    for (const note of record.outputNotes) {
      if (
        note.treeIndex === undefined ||
        !Number.isSafeInteger(note.treeIndex) ||
        note.treeIndex < 0
      ) {
        throw new Error("Settlement journal contains an invalid output tree");
      }
      treeIndexes.add(note.treeIndex);
    }
  }
  // The 64-record admission limit applies only to active records. Retained
  // wallet-rejection archives can legitimately span more historical trees;
  // do not let them disable active recovery. Trees stay sequential; the one
  // payout stream may overlap them. Every RPC range remains chunk-bounded.
  const insertions = new Map<string, FlatJoinSplitV2LeafInsertion>();
  const [, settlementHashes] = await Promise.all([
    (async () => {
      for (const treeIndex of treeIndexes) {
        const scan = await readJournalTreeHistory({
          provider: params.provider as any,
          pool: params.scope.poolAddress,
          treeIndex,
          fromBlock: deploymentBlock,
          toBlock: safeBlock,
          expectedToBlockHash: safeBlockHash,
          chunkSize,
        });
        for (const insertion of scan.insertions) {
          insertions.set(insertion.commitment.toLowerCase(), insertion);
        }
      }
    })(),
    scanSettlementEventHashes({
      provider: params.provider,
      pool: params.scope.poolAddress,
      records: standardRecords,
      fromBlock: deploymentBlock,
      toBlock: safeBlock,
      toBlockHash: safeBlockHash,
      chunkSize,
    }),
  ]);

  // Rejected attempts often reuse the same inputs. Share only this run's
  // reads at its hash-pinned safe block; never retain a spent-state decision.
  const nullifierReads = new Map<string, Promise<boolean>>();
  const readNullifier = (pool: string, nullifier: string) => {
    const key = `${pool.toLowerCase()}:${nullifier.toLowerCase()}`;
    let read = nullifierReads.get(key);
    if (!read) {
      read = nullifierUsed(params.provider, pool, nullifier, safeBlock);
      nullifierReads.set(key, read);
    }
    return read;
  };

  const finalized: FlatJoinSplitV2JournalRecord[] = [];
  const ambiguous: FlatJoinSplitV2JournalRecord[] = [];
  const superseded: FlatJoinSplitV2SupersededRecord[] = [];
  const issues = new Map<string, string>();
  const reasons = new Map<string, string>();
  const defer = (record: FlatJoinSplitV2JournalRecord, reason: string) => {
    reasons.set(record.id, reason);
    ambiguous.push(record);
  };
  for (const record of unresolved) {
    try {
      if (
        record.kind === "swap" &&
        decodePrivateSwap(record.transaction.data)
      ) {
        const completed = await reconcilePrivateSwapJournal(
          params.provider,
          params.scope,
          record,
          safeBlock,
          safeBlockHash,
          deploymentBlock,
        );
        if (completed) finalized.push(completed);
        else defer(record, "the swap has not settled at the safe block yet");
        continue;
      }
      if (record.kind === "bridge") bridgeIntent(record);
      const outputInsertions = record.outputNotes.map((note) =>
        insertionFor(insertions, note),
      );
      const insertedCount = outputInsertions.filter(Boolean).length;
      if (insertedCount > 0 && insertedCount !== outputInsertions.length) {
        throw new Error("only a subset of atomic settlement outputs was found");
      }
      const inputSpent = await Promise.all(
        record.sourceNullifiers.map((nullifier) =>
          readNullifier(record.poolAddress, nullifier),
        ),
      );
      const allNullifiersUsed = inputSpent.every(Boolean);
      const usedNullifiers = record.sourceNullifiers.filter(
        (_nullifier, index) => inputSpent[index],
      );
      // Retained conflicts remain eligible for output discovery after restart,
      // but do not repeatedly report an impossible intent as awaiting submission.
      if (
        record.state === "input_superseded" &&
        insertedCount === 0 &&
        record.inputConflict &&
        usedNullifiers.includes(record.inputConflict.nullifier)
      )
        continue;
      if (
        insertedCount === 0 &&
        usedNullifiers.length > 0 &&
        ["prepared", "broadcast", "included"].includes(record.state)
      ) {
        const { findFinalizedFlatSettlementInputConflict } =
          await import("./settlementInputConflict");
        const conflict = await findFinalizedFlatSettlementInputConflict({
          provider: params.provider,
          record,
          outputInsertions,
          usedNullifiers,
          deploymentBlock,
          safeBlock,
          safeBlockHash,
          chunkSize,
        });
        if (conflict) {
          await pinnedBlock(params.provider, safeBlock, safeBlockHash);
          const retained = await markFlatJoinSplitV2SettlementInputSuperseded(
            params.scope,
            record.id,
            record.calldataHash,
            conflict,
          );
          superseded.push({ record: retained, ...conflict });
          continue;
        }
      }
      if (
        !allNullifiersUsed ||
        (record.outputNotes.length > 0 && insertedCount === 0)
      ) {
        if (
          record.kind === "deposit" &&
          !allNullifiersUsed &&
          insertedCount === 0
        ) {
          const merge = depositMergeEvidenceFromJournal(record);
          if (merge) {
            // Wall-clock expiry cannot release an exposed proof. Require the
            // chain's finalized head, pinned to the same scanned history.
            const finalizedHead = await params.provider.getBlock("finalized");
            if (
              finalizedHead &&
              Number.isSafeInteger(finalizedHead.number) &&
              finalizedHead.number >= deploymentBlock &&
              ethers.isHexString(finalizedHead.hash, 32)
            ) {
              const expiryBlock = Math.min(safeBlock, finalizedHead.number);
              const expectedHash =
                expiryBlock === safeBlock
                  ? safeBlockHash
                  : finalizedHead.hash.toLowerCase();
              const block = await params.provider.getBlock(expiryBlock);
              if (
                block?.number === expiryBlock &&
                block.hash?.toLowerCase() === expectedHash &&
                Number.isSafeInteger(block.timestamp) &&
                BigInt(block.timestamp) > merge.deadline &&
                !(await nullifierUsed(
                  params.provider,
                  record.poolAddress,
                  merge.sourceNullifier,
                  expiryBlock,
                ))
              ) {
                await pinnedBlock(params.provider, expiryBlock, expectedHash);
                await pinnedBlock(params.provider, safeBlock, safeBlockHash);
                await retireExpiredFlatDepositMergeJournalRecord(
                  params.scope,
                  record.id,
                  block.timestamp,
                );
                continue;
              }
            }
          }
        }
        defer(
          record,
          !allNullifiersUsed
            ? `its inputs are not spent at safe block ${safeBlock} yet`
            : `none of its outputs are in the tree at safe block ${safeBlock} yet`,
        );
        continue;
      }

      const discoveredHashes = new Set([
        ...outputInsertions
          .map((insertion) => insertion?.transactionHash?.toLowerCase())
          .filter((hash): hash is string => Boolean(hash)),
        ...((WITHDRAWAL_EVIDENCE_KINDS.includes(record.kind) &&
          record.outputNotes.length === 0) ||
        record.kind === "swap" ||
        record.kind === "bridge"
          ? (settlementHashes.get(record.id) ?? [])
          : []),
      ]);
      if (discoveredHashes.size > 1) {
        throw new Error(
          "settlement evidence appeared in different transactions",
        );
      }
      const transactionHash =
        [...discoveredHashes][0] || record.txHash?.toLowerCase();
      if (!transactionHash) {
        defer(record, "no settlement transaction is known for it yet");
        continue;
      }
      const [receipt, transaction] = await Promise.all([
        params.provider.getTransactionReceipt(transactionHash),
        params.provider.getTransaction(transactionHash),
      ]);
      // A changeless withdrawal surfaces only as its payout event. When the
      // event that consumed exactly this record's nullifiers belongs to a
      // final transaction settling them under another proof, this record can
      // never execute and has no output to recover: close it instead of
      // re-checking it on every refresh.
      if (
        receipt &&
        receipt.hash.toLowerCase() === transactionHash &&
        receipt.status === 1 &&
        receipt.blockNumber <= safeBlock &&
        transaction &&
        transaction.hash?.toLowerCase() === transactionHash &&
        WITHDRAWAL_EVIDENCE_KINDS.includes(record.kind) &&
        record.outputNotes.length === 0 &&
        settlementHashes.get(record.id)?.has(transactionHash) &&
        !exactTransactionMatches(transaction, record) &&
        spendsSameInputsUnderAnotherProof(transaction, record)
      ) {
        await pinnedBlock(
          params.provider,
          receipt.blockNumber,
          receipt.blockHash.toLowerCase(),
        );
        await retireSupersededFlatJoinSplitV2SettlementJournalRecord(
          params.scope,
          record.id,
        );
        superseded.push({
          record,
          txHash: transactionHash,
          blockNumber: receipt.blockNumber,
        });
        continue;
      }
      if (
        !receipt ||
        receipt.hash.toLowerCase() !== transactionHash ||
        receipt.status !== 1 ||
        receipt.blockNumber > safeBlock ||
        !transaction ||
        (record.kind === "bridge" &&
          transaction.hash?.toLowerCase() !== transactionHash) ||
        (record.kind !== "deposit" &&
          !exactTransactionMatches(transaction, record))
      ) {
        defer(
          record,
          !receipt
            ? "its transaction receipt is not available yet"
            : receipt.blockNumber > safeBlock
              ? `its transaction is not final at safe block ${safeBlock} yet`
              : receipt.status !== 1
                ? "its transaction reverted"
                : !transaction
                  ? "its transaction is not available yet"
                  : mismatchReason(transaction, record),
        );
        continue;
      }
      const receiptBlockHash = await pinnedBlock(
        params.provider,
        receipt.blockNumber,
      );
      if (receiptBlockHash !== receipt.blockHash.toLowerCase()) {
        throw new Error("settlement receipt is no longer canonical");
      }
      if (record.kind === "deposit") {
        const note = depositNoteFromJournal(record);
        await assertFlatJoinSplitV2Note({
          note,
          chainId: record.chainId,
          poolAddress: record.poolAddress,
          tokenAddress: note.tokenData.tokenAddress,
        });
        const merge = depositMergeEvidenceFromJournal(record);
        const location = merge
          ? await verifyEvmDepositMergeReceipt({
              provider: params.provider,
              receipt,
              chainId: record.chainId,
              pool: record.poolAddress,
              note,
              sourceNullifier: merge.sourceNullifier,
              netCredit: merge.netCredit,
            })
          : await verifyFreshPoolDepositReceipt({
              provider: params.provider,
              receipt,
              chainId: record.chainId,
              pool: record.poolAddress,
              note,
            });
        const insertion = outputInsertions[0];
        if (
          !insertion ||
          insertion.treeIndex !== location.treeIndex ||
          insertion.leafIndex !== location.leafIndex ||
          insertion.blockNumber !== receipt.blockNumber ||
          insertion.blockHash.toLowerCase() !== receipt.blockHash.toLowerCase()
        ) {
          throw new Error("Deposit receipt and scanned leaf location disagree");
        }
      }
      if (record.kind === "bridge") requireBridgeReceipt(record, receipt);
      const locatedOutputs = record.outputNotes.map((note, index) => {
        const insertion = outputInsertions[index];
        return insertion ? { ...note, leafIndex: insertion.leafIndex } : note;
      });
      const swapOutputNote = record.swapOutputSeed
        ? await recoverFlatJoinSplitV2SwapOutput({
            provider: params.provider,
            receipt,
            pool: record.poolAddress,
            chainId: record.chainId,
            calldata: recoveryPoolInnerCall(record.transaction.data),
            seed: record.swapOutputSeed,
          })
        : undefined;
      await pinnedBlock(params.provider, receipt.blockNumber, receiptBlockHash);
      const updated = await markFlatJoinSplitV2SettlementFinalized(
        params.scope,
        record.id,
        {
          txHash: receipt.hash,
          blockNumber: receipt.blockNumber,
          blockHash: receipt.blockHash,
          outputNotes: locatedOutputs,
          swapOutputNote,
        },
      );
      finalized.push(updated);
    } catch (error) {
      issues.set(
        record.id,
        error instanceof Error ? error.message : String(error),
      );
      defer(record, error instanceof Error ? error.message : String(error));
    }
  }
  await pinnedBlock(params.provider, safeBlock, safeBlockHash);
  return {
    records,
    finalized,
    ambiguous,
    superseded,
    issues,
    reasons,
    safeBlock,
    safeBlockHash,
  };
}
